/**
 * AddSkillNudgeRow
 *
 * "Teach ‹agent› about Jam SQL: run `jam-sql add-skill`" — a dismissible row
 * in the AI terminal context bar's expanded section, shown when a detected
 * agent CLI (Claude Code or Codex) is missing the jam-sql skill (Task T15,
 * AI Smoothness Wave, D4). Extracted into its own file per the
 * ≤200-line-file guidance rather than growing AiTerminalContextBar.tsx.
 *
 * Detection reuses `useCliDetection()` (the same hook `AgentStartButtons`
 * and the AI-chat launchers use) rather than a second hand-rolled
 * `!available && !loading` expression — a duplicated copy of that predicate
 * is a defect this wave already fixed once.
 *
 * Visibility contract (F0.15: a false nudge is worse than a missing one):
 *   - hidden until CLI detection has produced a result — `useCliDetection()`
 *     guarantees `result` is null exactly while `isLoading` is true, so
 *     gating on `result` alone (no separate `!isLoading` check) already
 *     covers "don't flash the row before detection settles" without a
 *     redundant second condition.
 *   - hidden when no agent CLI is detected
 *   - hidden when the user already dismissed it (`nudges.addSkillDismissed`)
 *     — checked independently of the skill-install check below (dismissal
 *     hides the row even if a check is mid-flight or already resolved), so
 *     it is a real, load-bearing gate rather than one that merely happens
 *     to agree with another condition.
 *   - hidden per agent unless THAT agent's skill-install check has
 *     positively resolved to "not installed" — fix round 1 (I-1): the row
 *     used to pick the first *detected* agent regardless of its own
 *     install state, so two-agents-detected-but-only-one-missing-the-skill
 *     (the expected state for anyone who ran `jam-sql add-skill`'s
 *     per-agent picker once) silently never nudged the other agent. Each
 *     detected agent's check is tracked independently; the candidate is the
 *     first (stable-order) detected agent CONFIRMED lacking the skill.
 *
 * Claude Code and Codex are co-equal elsewhere in this feature, but the row
 * itself is singular (one dismissible row, one `addSkillDismissed` flag —
 * not per-agent). When both are detected and BOTH are confirmed lacking the
 * skill, Claude Code wins the tie — the same stable ordering
 * `AgentStartButtons`/`detectAgents()` already use elsewhere in this area.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, Copy, X } from 'lucide-react';
import { useCliDetection } from '../../ai-chat/hooks/useClaudeCliDetection';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { useAppStore } from '../../../store/appStore';
import { writeClipboardText } from '../../../lib/clipboard';
import type { TerminalSession } from '../../../types/terminal';
import type { SkillAgentId } from '../../../types/electron';

const ADD_SKILL_COMMAND = 'jam-sql add-skill';
const COPIED_RESET_MS = 2000;

type AgentCandidate = {
  /**
   * Key into useCliDetection()'s result AND the analytics event's `agent`
   * property — matches the sibling events `feature:ai_terminal_start_click`
   * / `feature:ai_terminal_agent_started` (fix round 1, M-5). NOT the same
   * id space as `skillAgentId` below.
   */
  detectKey: 'claude' | 'codex';
  /** The jam-sql skill installer's agent key (`add_skill.go`'s `agents` map) — used ONLY for the isSkillInstalled IPC call, never sent to analytics. */
  skillAgentId: SkillAgentId;
  displayName: string;
};

// Stable order: Claude Code first, then Codex — matches AgentStartButtons /
// electron/terminal/agentDetection.ts's detectAgents().
const AGENT_CANDIDATES: AgentCandidate[] = [
  { detectKey: 'claude', skillAgentId: 'claude-code', displayName: 'Claude Code' },
  { detectKey: 'codex', skillAgentId: 'codex', displayName: 'Codex' },
];

type AddSkillNudgeRowProps = {
  session: TerminalSession;
};

export function AddSkillNudgeRow({ session }: AddSkillNudgeRowProps) {
  const { result } = useCliDetection();
  const dismissed = useAppStore((s) => s.aiTerminalSettings.nudges.addSkillDismissed);
  const setAiTerminalSettings = useAppStore((s) => s.setAiTerminalSettings);
  const { track } = useAnalytics();
  // Per-agent: true/false once that agent's check resolves; absent while
  // unchecked, still in flight, or the IPC call failed. Never defaults an
  // unresolved agent to "installed" or "not installed".
  const [installedByAgent, setInstalledByAgent] = useState<Partial<Record<SkillAgentId, boolean>>>({});
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const detectedCandidates = result ? AGENT_CANDIDATES.filter((c) => result[c.detectKey]?.found) : [];
  // Cheap, stable dep for the effect below — avoids re-running on every
  // `result` object identity change (the hook returns a fresh object each
  // time detection completes) when the actual detected set hasn't changed.
  const detectedKey = detectedCandidates.map((c) => c.skillAgentId).join(',');

  useEffect(() => {
    if (detectedCandidates.length === 0) return;

    let cancelled = false;
    for (const candidate of detectedCandidates) {
      window.electronAPI?.terminal
        ?.isSkillInstalled?.({ agent: candidate.skillAgentId, projectCwd: session.workspacePath })
        .then((isInstalled) => {
          if (cancelled) return;
          setInstalledByAgent((prev) => ({ ...prev, [candidate.skillAgentId]: isInstalled }));
        })
        .catch(() => {
          // Stay silent — never claim "not installed" from a failed check.
        });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detectedKey stands in for detectedCandidates' content
  }, [detectedKey, session.workspacePath]);

  // Unmount cleanup for the copied-state timeout — same post-unmount-write
  // class T13b fixed for DataCompareResultsGrid's fetch generation guard.
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const candidate = detectedCandidates.find((c) => installedByAgent[c.skillAgentId] === false);

  // `dismissed` is checked here — independent of whether `candidate` is
  // set — so it genuinely gates visibility (removing it would let the row
  // reappear on the next detection/check tick even after dismissal).
  if (!candidate || dismissed) {
    return null;
  }

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await writeClipboardText(ADD_SKILL_COMMAND);
    if (!ok) {
      // Best-effort — no success event or visible error on a failed copy.
      return;
    }
    setCopied(true);
    track('feature:ai_terminal_add_skill_copy', { agent: candidate.detectKey });
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAiTerminalSettings({ nudges: { addSkillDismissed: true } });
    track('feature:ai_terminal_add_skill_dismissed', { agent: candidate.detectKey });
  };

  return (
    <div
      className="mt-2 flex items-center justify-between gap-2 rounded border border-border/50 bg-background/50 p-2"
      data-testid="ai-terminal-add-skill-nudge"
    >
      <span className="text-muted-foreground">
        Teach {candidate.displayName} about Jam SQL: run{' '}
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-foreground">{ADD_SKILL_COMMAND}</code>
      </span>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={handleCopy}
          title="Copy command"
          aria-label="Copy command"
          data-testid="ai-terminal-add-skill-copy"
          className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
        >
          {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          title="Dismiss"
          aria-label="Dismiss"
          data-testid="ai-terminal-add-skill-dismiss"
          className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
